import classNames from "classnames";
import BaseButton from "components/baseButton/baseButton";
import BaseCheckbox from "components/baseCheckbox/baseCheckbox";
import BaseInput from "components/baseInput/baseInput";
import BaseMultiselect, { Option } from "components/baseMultiselect/baseMultiselect";
import { useFormik } from "formik";
import { t } from "i18next";
import { Form, Modal } from "react-bootstrap";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { confirmModal, declineModal } from "redux/reducers/modalReducer";
import { executeAxiosRequestWithRefresh } from "redux/services";
import { RootState, useAppDispatch } from "redux/store";
import { ModalTypeEnum } from "utils/enums";
import { StringResources } from "utils/language/languageResource";
import { PageTypeEnum } from "utils/reportDefinitions";
import * as Yup from "yup";
import "./createOrEditApiKeyModal.scss";
import BaseBanner from "components/baseBanner/baseBanner";

interface ICreateOrEditApiKeyModalProps {
    title: string;
    actionButtonText: string;
    data: {
        keyName?: string;
        apiKeyId?: number;
        isSubscribedToAllContracts?: boolean;
        partnerAgreementIds?: Array<string>;
    };
}

const CreateOrEditApiKeyModal = ({ title, actionButtonText, data }: ICreateOrEditApiKeyModalProps) => {
    const dispatch = useAppDispatch();
    const partnerContracts = useSelector((state: RootState) => state.global.partnerContracts);

    const validationSchema = Yup.object().shape({
        keyName: Yup.string().required(t(StringResources.modal.apiKeys.keyNameIsRequired)),
        partnerAgreementIds: Yup.array().when("isSubscribedToAllContracts", {
            is: false,
            then: Yup.array().min(1, t(StringResources.modal.apiKeys.partnerAgreementsIsRequired)),
        }),
    });

    const formValidation = useFormik({
        initialValues: {
            keyName: data.keyName || "",
            isSubscribedToAllContracts: data.isSubscribedToAllContracts || false,
            partnerAgreementIds:
                data.partnerAgreementIds || []
                    ? partnerContracts
                          .filter(agreement => (data.partnerAgreementIds || []).includes(agreement.number))
                          .map(agreement => agreement.id)
                    : [],
        },
        validationSchema,
        onSubmit: async values => {
            try {
                if (data.apiKeyId) {
                    // Edit existing API key
                    await executeAxiosRequestWithRefresh({
                        url: `/api/users/update-api-key`,
                        method: "POST",
                        data: {
                            apiKeyId: data.apiKeyId,
                            keyName: values.keyName,
                            isSubscribedToAllContracts: values.isSubscribedToAllContracts,
                            partnerContractIds: values.isSubscribedToAllContracts ? [] : values.partnerAgreementIds,
                        },
                    });
                    toast.success(t(StringResources.pages[PageTypeEnum.ApiKeys].editKeySuccess) as string);
                } else {
                    // Create new API key
                    await executeAxiosRequestWithRefresh({
                        url: "/api/users/generate-new-api-key",
                        method: "POST",
                        data: {
                            keyName: values.keyName,
                            isSubscribedToAllContracts: values.isSubscribedToAllContracts,
                            partnerContractIds: values.isSubscribedToAllContracts ? [] : values.partnerAgreementIds,
                        },
                    });
                    toast.success(t(StringResources.pages[PageTypeEnum.ApiKeys].generateNewKeySuccess) as string);
                }

                dispatch(confirmModal({ modalType: ModalTypeEnum.CreateOrEditApiKey }));
            } catch (error) {
                const errorMessage = data.apiKeyId
                    ? t(StringResources.pages[PageTypeEnum.ApiKeys].editKeyError)
                    : t(StringResources.pages[PageTypeEnum.ApiKeys].generateNewKeyError);
                toast.error(errorMessage);
            }
        },
    });

    const partnerAgreementOptions: Option[] = partnerContracts.map(
        (agreement: { id: number; number: string; name?: string }) => ({
            value: agreement.id,
            label: `${agreement.number}${agreement.name ? " - " + agreement.name : ""}`,
            selected: formValidation.values["partnerAgreementIds"].includes(agreement.id),
        })
    );

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        formValidation.handleSubmit();
    };

    const handleClose = () => {
        dispatch(declineModal({ modalType: ModalTypeEnum.CreateOrEditApiKey }));
    };

    return (
        <Modal show={true} onHide={handleClose} className="create-edit-api-keys" dialogClassName="base-modal" size="sm">
            <Form onSubmit={handleSubmit} noValidate>
                <Modal.Header closeButton className={classNames({ "modal-border-bottom": true })}>
                    <Modal.Title>{title}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <BaseBanner
                        text={`${t(StringResources.pages[PageTypeEnum.ApiKeys].generateNewKeyBannerMessage)}`}
                    />

                    <BaseInput
                        type={"text"}
                        name={"keyName"}
                        autocomplete={"off"}
                        label={`${t(StringResources.pages[PageTypeEnum.ApiKeys].keyName)}`}
                        value={formValidation.values["keyName"]}
                        invalid={
                            formValidation.touched["keyName"] && typeof formValidation.errors["keyName"] === "string"
                                ? formValidation.errors["keyName"]
                                : undefined
                        }
                        onBlur={formValidation.handleBlur}
                        onChange={formValidation.handleChange}
                    />
                    <BaseMultiselect
                        label={`${t(StringResources.modal.apiKeys.partnerAgreements)}`}
                        options={partnerAgreementOptions}
                        disabled={formValidation.values["isSubscribedToAllContracts"]}
                        onChange={selectedOptions => {
                            formValidation.setFieldValue(
                                "partnerAgreementIds",
                                selectedOptions.map(option => option.value)
                            );
                        }}
                    />
                    <BaseCheckbox
                        id="isSubscribedToAllContracts"
                        label={`${t(StringResources.pages[PageTypeEnum.ApiKeys].isSubscribedToAllContracts)}`}
                        checked={formValidation.values["isSubscribedToAllContracts"]}
                        onChange={(_id, checked) => formValidation.setFieldValue("isSubscribedToAllContracts", checked)}
                    />
                </Modal.Body>
                <Modal.Footer>
                    <BaseButton handleClick={handleClose} text={t(StringResources.modal.cancel)} styleType="line" />
                    <BaseButton
                        handleClick={() => formValidation.handleSubmit()}
                        text={actionButtonText}
                        styleType="solid"
                        disabled={!formValidation.isValid || formValidation.isSubmitting}
                    />
                </Modal.Footer>
            </Form>
        </Modal>
    );
};

export default CreateOrEditApiKeyModal;
